/**
 * page-link-drawer.js
 *
 * Enhances "Collapsible text" blocks flagged with the `js-page-links-drawer`
 * class so that any link pointing to a Shopify store page (URL containing
 * `/pages/`) opens the page content inside the sidebar <x-drawer> that is
 * rendered alongside the accordion, instead of navigating away.
 *
 * The <x-drawer> is rendered server-side in Liquid (so its shadow DOM template
 * is built correctly by the theme). This script only fills it and opens it.
 *
 * The link is authored normally in the rich text from the theme editor / page
 * back end: `<a href="/pages/shipping">Shipping</a>`. No special markup needed.
 */
(function () {
  "use strict";

  var SELECTOR_ROOT = ".js-page-links-drawer";
  var PAGE_URL_PATTERN = /\/pages\//;
  var DEBUG = true; // set to false to silence console logs once verified
  var pageCache = {};

  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ["[page-link-drawer]"].concat([].slice.call(arguments)));
    }
  }

  function shouldIntercept(anchor) {
    if (!anchor || !anchor.getAttribute("href")) return false;
    if (anchor.hasAttribute("data-no-drawer")) return false;

    var url;
    try {
      url = new URL(anchor.href, window.location.origin);
    } catch (e) {
      return false;
    }

    if (url.origin !== window.location.origin) return false;
    return PAGE_URL_PATTERN.test(url.pathname);
  }

  /**
   * Given a clicked link, find the <x-drawer> rendered for its accordion.
   * The drawer is a sibling of the accordion inside the same block wrapper,
   * so we look within the closest block container. Falls back to any drawer
   * flagged with [data-page-link-drawer] on the page.
   */
  function findDrawerFor(anchor) {
    var root = anchor.closest(SELECTOR_ROOT);
    var scope = root ? root.parentElement : document;
    var drawer =
      (scope && scope.querySelector("[data-page-link-drawer]")) ||
      document.querySelector("[data-page-link-drawer]");
    return drawer;
  }

  function extractPageContent(doc) {
    var contentEl =
      doc.querySelector("main [class*='prose']") ||
      doc.querySelector("[class*='prose']") ||
      doc.querySelector("main .shopify-section") ||
      doc.querySelector("main") ||
      doc.querySelector("[role='main']") ||
      doc.body;

    var titleEl =
      doc.querySelector("main h1") ||
      doc.querySelector("h1");

    return {
      title: titleEl ? titleEl.textContent.trim() : doc.title,
      html: contentEl ? contentEl.innerHTML : ""
    };
  }

  function openDrawer(drawer, data) {
    var titleEl = drawer.querySelector("[data-page-link-drawer-title]");
    var contentEl = drawer.querySelector("[data-page-link-drawer-content]");
    if (titleEl) titleEl.textContent = data.title || "";
    if (contentEl) contentEl.innerHTML = data.html || "";

    if (typeof drawer.show === "function") {
      log("opening drawer via show()");
      drawer.show();
    } else {
      log("show() not available yet, waiting for x-drawer to be defined");
      window.customElements.whenDefined("x-drawer").then(function () {
        if (typeof drawer.show === "function") {
          drawer.show();
        } else {
          drawer.setAttribute("open", "");
        }
      });
    }
  }

  function loadAndOpen(drawer, pathname) {
    if (pageCache[pathname]) {
      log("using cached content for", pathname);
      openDrawer(drawer, pageCache[pathname]);
      return Promise.resolve();
    }

    log("fetching", pathname);
    return fetch(pathname, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (response) {
        if (!response.ok) throw new Error("Request failed: " + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var data = extractPageContent(doc);
        log("extracted content", { title: data.title, htmlLength: data.html.length });
        pageCache[pathname] = data;
        openDrawer(drawer, data);
      });
  }

  function onClick(event) {
    var anchor = event.target.closest ? event.target.closest("a[href]") : null;
    if (!anchor) return;
    if (!anchor.closest(SELECTOR_ROOT)) return;
    if (!shouldIntercept(anchor)) {
      log("link not intercepted (not a same-origin /pages/ link):", anchor.getAttribute("href"));
      return;
    }

    var drawer = findDrawerFor(anchor);
    if (!drawer) {
      log("no drawer element found for this accordion, letting the link navigate");
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var pathname = new URL(anchor.href, window.location.origin).pathname;

    loadAndOpen(drawer, pathname).catch(function (error) {
      log("failed, falling back to navigation:", error);
      window.location.href = anchor.href;
    });
  }

  // Capture phase so we run before other click handlers (e.g. analytics that
  // may open the link in a new tab) and can preventDefault reliably.
  document.addEventListener("click", onClick, true);
  log("initialized");
})();
